// src/sim/grab.ts — Tutma FSM (DESIGN §4.1 G1/G1b/G2/G3/C, §6, §7; ARCHITECTURE §3 adım 5): G1 teması (hits.ts hitbox → tryGrab), Hold
// 15 t kilidi (B → Throw: grabStun ≥ 40 hemen, < 40 en erken 15. t; B yoksa omuza alma 26 t), Throw (bırakma 19 t, fırlatma vektörü,
// fırlatılan beden mermisi), Lifting → Carry (×0.55 hız fighter.ts; süre aşımı YOK; L1 bırak / B fırlat / Y omuz çarpması
// moves.ts), G3 yerden kaldırma (16. t kavrama, 38 t), kurtulma (windup: 34 t / 26 t içinde 4 basış ∧ grabStun < 40; Carry:
// 48 t koruma penceresi + 6 basış mash, zamanla otomatik kurtulma yok), üçüncü kişi koparması (hits.ts → breakGrab). three'siz, tahsissiz.
//
// Held / Carried kurbanın konumu her tick tutanın soketine yazılır (adım 6 bu durumlarda hareket etmez). Throwing / PickingUp durumlarını
// items.ts (silah fırlatma / eşya alma) da kullanır → ayrım `f.grabTarget !== NO_ID`; items.step `tick + 1 ≥ stateUntil`te Idle'a çektiği
// için tutma sürelerinde stateUntil = kendi bitişim + 1 (geçiş adım 5'te önce bende olur).
import { Btn, Ev, FState, HitKind } from '../core/types.ts';
import type { Fighter, SimWorld } from '../core/types.ts';
import { fwdX, fwdZ, localToWorld, wrapAngle, yawOf } from '../core/vec3.ts';
import { clamp } from '../core/math.ts';
import { CAP_R, HURT_R, NO_ID, NO_TICK, TICK_HZ } from '../config/constants.ts';
import { FACE_BUTTONS, btnIndex } from '../config/controls.ts';
import { T } from '../config/tuning.ts';
import { MOVE_ID, moveIndexOf } from '../config/moves.ts';
import { clearMove, enterHitstun, enterLying, enterTumble } from './knockdown.ts';

/** 2B uzunluk: Math.hypot V8'de FixedDoubleArray tahsis eder (sıcak yol yasağı; match.ts kalıbı) → sqrt(a²+b²). */
const hyp = (a: number, b: number): number => Math.sqrt(a * a + b * b);

const G2_IDX = moveIndexOf(MOVE_ID.G2);
const G3_IDX = moveIndexOf(MOVE_ID.G3);
const THROW_TOTAL = T.GRAB.throwReleaseTick + 1 + 15;   // G2: 19 / 1 / 15 (sonra hareket)
const CARRIED_UP = 0.973 - 0.2;   // chest dinlenme 0.973 + attachUp −0.20 (ANIMATION §6.5) — sim kurban kökü yüksekliği
const ESCAPE_TICKS = T.DODGE.ticks;   // Dodge_Backward tS 1.2 ≈ 19 t (ayakta tutuştan kurtulma sıçraması)
const BODY_R = T.ITEMS.throwRadius;   // fırlatılan beden mermi r 0.6
/** Omuzdan kurtulan kurbanın tutana uzaklığı: bir kapsül boyu (iki yarıçap) + pay — "yakınında ayağa kalkar" (DESIGN §6). */
const CARRY_ESCAPE_GAP = CAP_R * 2 + 0.15;

function setState(f: Fighter, s: FState, tick: number, until: number = NO_TICK): void { f.state = s; f.stateTick = tick; f.stateUntil = until; }
function fighter(world: SimWorld, id: number): Fighter | undefined { return id >= 0 ? world.fighters[id] : undefined; }
function pressedWithin(world: SimWorld, f: Fighter, btn: Btn, window: number): boolean {
  const t = world.buffers[f.id]!.pressTick[btnIndex(btn)]!;
  return t !== NO_TICK && world.tick - t <= window;
}
function pressedSince(world: SimWorld, f: Fighter, btn: Btn, since: number): boolean {
  const t = world.buffers[f.id]!.pressTick[btnIndex(btn)]!;
  return t !== NO_TICK && t >= since;
}
function consume(world: SimWorld, f: Fighter, btn: Btn): void { world.buffers[f.id]!.pressTick[btnIndex(btn)] = NO_TICK; }

/** Ayak yüksekliği (zemine göre); zemin yoksa (platform dışı) sonsuz → tutulamaz. */
function footHeight(world: SimWorld, f: Fighter): number {
  const g = world.query.groundY(f.pos.x, f.pos.z, f.pos.y + 0.1);
  return Number.isNaN(g) ? Infinity : f.pos.y - g;
}
/** Tutulabilir: ayakta durum ∧ ayak ≤ 0.5 u (hook tepesi 0.31 tutulur; havadaki tutulamaz) ∧ tutma ilişkisinde değil. */
export function grabbable(world: SimWorld, b: Fighter): boolean {
  switch (b.state) {
    case FState.Lying: case FState.Crawl: case FState.Roll: case FState.Knockdown: case FState.Tumble: case FState.GetUp:
    case FState.Held: case FState.Carried: case FState.Hold: case FState.Lifting: case FState.Carrying: case FState.Escaping:
    case FState.Spawning: case FState.Respawning: case FState.Falling: case FState.Eliminated:
      return false;
    default: break;
  }
  if (b.grabTarget !== NO_ID || b.grabbedBy !== NO_ID) return false;
  return footHeight(world, b) <= T.COMBAT.grabMaxFootHeight;
}

// ───────────────────────────── soket konumları ─────────────────────────────
function placeHeld(a: Fighter, b: Fighter): void {
  b.pos.x = a.pos.x + fwdX(a.yaw) * T.GRAB.holdDist; b.pos.z = a.pos.z + fwdZ(a.yaw) * T.GRAB.holdDist; b.pos.y = a.pos.y;
  b.yaw = wrapAngle(a.yaw + Math.PI);
  b.vel.x = 0; b.vel.y = 0; b.vel.z = 0; b.rmDx = 0; b.rmDz = 0; b.grounded = true;
}
function placeCarried(a: Fighter, b: Fighter): void {
  localToWorld(b.pos, a.pos, a.yaw, T.ANIM.attachFwd, CARRIED_UP, T.ANIM.attachSide);
  b.yaw = wrapAngle(a.yaw + T.ANIM.attachYawDeg * Math.PI / 180);
  b.vel.x = 0; b.vel.y = 0; b.vel.z = 0; b.rmDx = 0; b.rmDz = 0; b.grounded = true;
}

// ───────────────────────────── G1 teması ─────────────────────────────
/**
 * hits.ts: G1 hitbox'ı B'ye değdi. Döner: tutuş kuruldu mu. Havadaki (ayak > 0.5) tutulamaz; iki tutma aynı tick → ikisi Hit_A 18 t + 3 u/s.
 * Başarı: A Hold, B Held (Hit_A 0.20 paused; tutanın 1.1 u önünde, tutana bakar), grabStun = B.stun, silah düşer, Ev.Grab + Ev.Hold.
 */
export function tryGrab(world: SimWorld, a: Fighter, b: Fighter): boolean {
  const tick = world.tick;
  if (b.state === FState.GrabAttempt && b.phase === 2 /* Active */ && b.moveIdx >= 0) {
    // karşılıklı tutma: ikisi de Hit_A 0.30 s, 3 u/s itilir
    const dx = b.pos.x - a.pos.x, dz = b.pos.z - a.pos.z;
    const d = hyp(dx, dz);
    const nx = d > 1e-6 ? dx / d : fwdX(a.yaw), nz = d > 1e-6 ? dz / d : fwdZ(a.yaw);
    enterHitstun(world, a, T.GRAB.grabbedHolderStunTicks, HitKind.Light);
    enterHitstun(world, b, T.GRAB.grabbedHolderStunTicks, HitKind.Light);
    a.vel.x = -nx * T.GRAB.breakSeparation; a.vel.z = -nz * T.GRAB.breakSeparation;
    b.vel.x = nx * T.GRAB.breakSeparation; b.vel.z = nz * T.GRAB.breakSeparation;
    world.events.push(Ev.GrabBreak, tick, a.id, b.id);
    return false;
  }
  if (!grabbable(world, b)) return false;
  clearMove(b);
  b.tumbleControl = false; b.fastFall = false;
  a.touched = true;
  clearMove(a);
  a.grabTarget = b.id; a.holdSince = tick; a.holdDirX = fwdX(a.yaw); a.holdDirZ = fwdZ(a.yaw);
  a.vel.x = 0; a.vel.z = 0;
  b.grabbedBy = a.id; b.grabStun = b.stun; b.escapePresses = 0; b.holdSince = tick;
  b.windupPresses = 0;
  b.lastHitBy = a.id; b.lastHitTick = tick;
  a.yaw = yawOf(b.pos.x - a.pos.x, b.pos.z - a.pos.z) || a.yaw;
  setState(a, FState.Hold, tick, tick + T.GRAB.holdLockTicks);
  setState(b, FState.Held, tick);
  placeHeld(a, b);
  world.systems.items.drop(world, b, tick, Ev.Grab);   // tutulma → silah düşer (DESIGN §9)
  world.events.push(Ev.Grab, tick, a.id, b.id);
  world.events.push(Ev.Hold, tick, a.id, b.id);
  return true;
}

/** moves.ts: B ile yatan rakibi kaldırma (G3). A PickingUp 38 t; 16. t'de kavrama (kurban Carried), 38'de Carry. */
export function startPickUp(world: SimWorld, a: Fighter, b: Fighter): void {
  const tick = world.tick;
  clearMove(a);
  a.moveIdx = G3_IDX; a.moveTick = 0; a.phase = 1; a.stageIdx = 0; a.stageStartTick = tick;
  a.grabTarget = b.id; a.holdSince = tick;
  b.grabbedBy = a.id;
  a.vel.x = 0; a.vel.z = 0;
  a.yaw = yawOf(b.pos.x - a.pos.x, b.pos.z - a.pos.z) || a.yaw;
  setState(a, FState.PickingUp, tick, tick + T.GRAB.pickUpTotal + 1);
  const e = world.events.push(Ev.MoveStart, tick, a.id, b.id); e.move = G3_IDX;
}

/** Yatan aday (G3): ≤ 1.2 u, Lying/Crawl, kaldırılabilir (i-frame yok, tutulmamış). En yakın. */
export function findLiftTarget(world: SimWorld, a: Fighter): Fighter | undefined {
  let best: Fighter | undefined; let bestD = T.GRAB.pickUpRange;
  for (let i = 0; i < world.fighters.length; i++) {
    const b = world.fighters[i]!;
    if (b === a || (b.state !== FState.Lying && b.state !== FState.Crawl) || b.grabbedBy !== NO_ID) continue;
    if (b.invulnUntil > world.tick || b.spawnProtUntil > world.tick) continue;
    const d = hyp(b.pos.x - a.pos.x, b.pos.z - a.pos.z);
    if (d <= bestD) { bestD = d; best = b; }
  }
  return best;
}

// ───────────────────────────── kurtulma / koparma ─────────────────────────────
/**
 * Kurtulma. İki ayrı his (kullanıcı geri bildirimi, 5. tur):
 *  - **Ayakta tutuştan** (`fromCarry = false`, Hold / Throw windup): eskisi gibi Dodge_Backward ile 3 u geri (i-frame 12 t) —
 *    kurban henüz yakalanma anındadır, kendini geri atarak sıyrılır.
 *  - **Omuzdan** (`fromCarry = true`, Lifting'in 13. t'i sonrası ve Carry): 3 u sıçrama ve i-frame'li dodge görüntüsü YOK.
 *    Kurban tutanın hemen ARKASINDA (bir kapsül boyu) ayakta belirir ve tutana döner; kısa bir toparlanma (kontrol kilidi
 *    `carryEscapeRecoverTicks`) dışında serbesttir. NEDEN: oyuncu "C'ye basmış gibi uzağa kayıyor" diye şikâyet etti; animMap'te
 *    "omuzdan iniş" klibi yok → mevcut nötr duruşa (FState.Idle) dönülüyor, Carried → Idle geçişini animController fade'liyor.
 * Her iki durumda tutan Hit_A 0.35 s (21 t) yer — kurtulmanın tutan için bedeli.
 */
function escape(world: SimWorld, a: Fighter, b: Fighter, fromCarry: boolean): void {
  const tick = world.tick;
  const fx = fwdX(a.yaw), fz = fwdZ(a.yaw);
  detach(a, b);
  clearMove(b);
  b.vel.x = 0; b.vel.z = 0; b.vel.y = 0; b.grounded = true;
  if (fromCarry) {
    b.pos.x = a.pos.x - fx * CARRY_ESCAPE_GAP; b.pos.z = a.pos.z - fz * CARRY_ESCAPE_GAP; b.pos.y = a.pos.y;
    b.yaw = yawOf(fx, fz);   // tutanın arkasında, tutanın baktığı yöne bakar (sırt sırta değil, kovalayacak)
    b.escapeLeft = 0; b.escapePresses = 0;
    setState(b, FState.Idle, tick);
    // i-frame yok: tutan zaten 21 t sersem; kurbanın 12 t toparlanması ondan kısa → kurtulmak yine kârlı, ama "ışınlanma" yok
    b.controlLockUntil = Math.max(b.controlLockUntil, tick + T.GRAB.carryEscapeRecoverTicks);
  } else {
    b.dodgeDirX = fx; b.dodgeDirZ = fz;
    b.yaw = yawOf(-b.dodgeDirX, -b.dodgeDirZ);
    setState(b, FState.Escaping, tick, tick + ESCAPE_TICKS + 1);
    b.invulnUntil = tick + T.GRAB.escapeIframes;
  }
  enterHitstun(world, a, T.GRAB.holderStunTicks, HitKind.Light);
  world.events.push(Ev.Escape, tick, b.id, a.id);
}
function detach(a: Fighter, b: Fighter): void {
  a.grabTarget = NO_ID; b.grabbedBy = NO_ID;
  a.holdSince = NO_TICK; b.holdSince = NO_TICK; a.carryUntil = NO_TICK;
}
/** Carry'den düşürme (L1 / süre / omuz çarpması / 3. kişi): kurban önde Lie_Down → Lying (ticks), Ev.Drop. */
export function dropCarried(world: SimWorld, a: Fighter, lyingTicks: number): void {
  const b = fighter(world, a.grabTarget);
  if (!b) return;
  const tick = world.tick;
  detach(a, b);
  const fx = fwdX(a.yaw), fz = fwdZ(a.yaw);
  b.pos.x = a.pos.x + fx * (CAP_R * 2 + 0.2); b.pos.z = a.pos.z + fz * (CAP_R * 2 + 0.2); b.pos.y = a.pos.y;
  b.vel.x = 0; b.vel.y = 0; b.vel.z = 0; b.grounded = true;
  b.yaw = a.yaw;
  enterLying(world, b, lyingTicks, false);
  if (a.state === FState.Carrying) setState(a, FState.Idle, tick);
  world.events.push(Ev.Drop, tick, a.id, b.id);
}

/**
 * hits.ts: üçüncü kişi koparması. holder = tutan; hitParty 0 = tutan vuruldu (stunAdd ≥ 1), 1 = kurban vuruldu (stunAdd ≥ 20).
 * Vurulmayan taraf burada durum alır; vurulan tarafın durumunu çağıran (isabetin kendisi) yazar. Hold/Lifting: ikisi Hit_A 0.35 s + 3 u/s;
 * Carry (ve 16. t sonrası PickUp): kurban Lying ≥ 48 t, tutan Hit_A 0.35 s. PickUp ilk 16 t: yalnız iptal (kurban zaten Lying).
 */
export function breakGrab(world: SimWorld, holder: Fighter, hitParty: number): void {
  const b = fighter(world, holder.grabTarget);
  if (!b) return;
  const tick = world.tick;
  const fx = fwdX(holder.yaw), fz = fwdZ(holder.yaw);
  const standing = b.state === FState.Held;
  const lyingVictim = b.state === FState.Lying || b.state === FState.Crawl || b.state === FState.Roll;
  detach(holder, b);
  if (hitParty === 0) {
    // tutan vuruldu: kurban serbest
    if (standing) {
      enterHitstun(world, b, T.GRAB.holderStunTicks, HitKind.Light);
      b.vel.x = fx * T.GRAB.breakSeparation; b.vel.z = fz * T.GRAB.breakSeparation;
    } else if (!lyingVictim) {
      b.pos.x = holder.pos.x + fx * (CAP_R * 2 + 0.2); b.pos.z = holder.pos.z + fz * (CAP_R * 2 + 0.2); b.pos.y = holder.pos.y; b.yaw = holder.yaw;
      enterLying(world, b, Math.max(T.KNOCKDOWN.carryDropLyingThirdParty, T.KNOCKDOWN.carryBreakLyingMin), false);
    }
    holder.vel.x = -fx * T.GRAB.breakSeparation; holder.vel.z = -fz * T.GRAB.breakSeparation;
    if (holder.state === FState.Carrying || holder.state === FState.PickingUp) clearMove(holder);
    // tutanın kendi Hitstun'ını isabet yazar (hits.ts: en az holderStunTicks)
  } else {
    // kurban vuruldu (≥ 20): tutan Hit_A 0.35 s, kurban isabetin knockback'ini normal alır (çağıran)
    if (standing) { b.vel.x = fx * T.GRAB.breakSeparation; b.vel.z = fz * T.GRAB.breakSeparation; b.grounded = true; }
    else if (!lyingVictim) { b.pos.x = holder.pos.x + fx * (CAP_R * 2 + 0.2); b.pos.z = holder.pos.z + fz * (CAP_R * 2 + 0.2); b.pos.y = holder.pos.y; b.grounded = true; }
    clearMove(b);
    if (!lyingVictim) setState(b, FState.Idle, tick);
    enterHitstun(world, holder, T.GRAB.holderStunTicks, HitKind.Light);
    holder.vel.x = -fx * T.GRAB.breakSeparation; holder.vel.z = -fz * T.GRAB.breakSeparation;
  }
  world.events.push(Ev.GrabBreak, tick, holder.id, b.id);
}

// ───────────────────────────── fırlatma ─────────────────────────────
/** Hold ya da Carry'den Throw başlat: tutan Throwing (bırakma 19. t), yön = tam çubuk (nötr = bakış); geri/yan için 6 t'de döner. */
function startThrow(world: SimWorld, a: Fighter, b: Fighter): void {
  const tick = world.tick;
  const input = world.inputs[a.id]!;
  const mag = hyp(input.moveX, input.moveZ);
  if (mag > T.MOVE.walkDead) { a.holdDirX = input.moveX / mag; a.holdDirZ = input.moveZ / mag; }
  else { a.holdDirX = fwdX(a.yaw); a.holdDirZ = fwdZ(a.yaw); }
  clearMove(a);
  a.moveIdx = G2_IDX; a.moveTick = 0; a.phase = 1; a.stageIdx = 0; a.stageStartTick = tick;
  setState(a, FState.Throwing, tick, tick + THROW_TOTAL + 1);
  consume(world, a, Btn.Grab);
  const e = world.events.push(Ev.MoveStart, tick, a.id, b.id); e.move = G2_IDX;
}
/** Bırakma tick'i: DESIGN §6 vektörü (kb 6.5 %-ölçekli, tumble tavanı; lift 5), dmg 12, Tumble (fırlatılma → Lying 60 t), mermi. */
function releaseThrow(world: SimWorld, a: Fighter, b: Fighter): void {
  const tick = world.tick;
  const dmg = b.damage;
  let speed = T.GRAB.throwKb * (1 + dmg / 100 * T.COMBAT.kbDmgScale) * b.weightMult * (world.arena.halfWidth / 9);
  speed = Math.min(speed, T.COMBAT.tumbleCapBase + T.COMBAT.tumbleCapPerDmg * dmg / 100);
  const vy = T.GRAB.throwLift * (1 + dmg / 100 * T.COMBAT.liftDmgScale);
  const dx = a.holdDirX, dz = a.holdDirZ;
  detach(a, b);
  b.pos.x = a.pos.x + dx * T.GRAB.holdDist; b.pos.z = a.pos.z + dz * T.GRAB.holdDist; b.pos.y = a.pos.y + 0.05;
  b.vel.x = dx * speed; b.vel.z = dz * speed; b.vel.y = vy; b.grounded = false;
  b.damage = Math.min(T.COMBAT.dmgMax, b.damage + T.GRAB.throwDmg);
  b.lastHitBy = a.id; b.lastHitTick = tick; b.lastHitDirX = dx; b.lastHitDirZ = dz;
  const hitstun = Math.max(T.COMBAT.hitstunMinTicks, Math.round(TICK_HZ * clamp(T.COMBAT.hitstunBase + T.COMBAT.hitstunPerKb * speed, T.COMBAT.hitstunMin, T.COMBAT.hitstunMax)));
  enterTumble(world, b, hitstun, true);   // yön b.lastHitDirX/Z (yukarıda yazıldı)
  // Fırlatma hits.ts'ten GEÇMEZ (burada doğrudan tumble'a giriyor), o yüzden Ev.Downed'ı burada basıyoruz.
  // Kullanıcının istediği "aşağıya düşüren hamle" tam olarak budur — kucaklayıp boşluğa atmak en ödüllendirici an.
  { const d = world.events.push(Ev.Downed, tick, a.id, b.id); d.v0 = 0; d.v1 = 1; }
  b.thrownBy = a.id;
  a.hitstop = Math.max(a.hitstop, T.HITSTOP.finisher); b.hitstop = Math.max(b.hitstop, T.HITSTOP.finisher);
  b.stateUntil += T.HITSTOP.finisher; a.stateTick += T.HITSTOP.finisher; a.stateUntil += T.HITSTOP.finisher;   // donma süre yemez
  world.systems.items.drop(world, b, tick, Ev.Throw);
  const e = world.events.push(Ev.Throw, tick, a.id, b.id); e.v0 = speed; e.v1 = vy; e.dx = dx; e.dz = dz; e.move = G2_IDX;
  const h = world.events.push(Ev.Hitstop, tick, a.id, b.id); h.v0 = T.HITSTOP.finisher;
}

/** Fırlatılan beden mermisi (ARCHITECTURE §3 adım 7; hits.ts çağırır): havada ilk kapsül teması → bodyImpact (kb 8 / 2, dmg 5, stun 25, hitstop 5 ikisine). */
export function resolveThrownBodies(world: SimWorld): void {
  const tick = world.tick;
  for (let i = 0; i < world.fighters.length; i++) {
    const b = world.fighters[i]!;
    const thrower = b.thrownBy;
    if (thrower === NO_ID) continue;
    if (b.state !== FState.Tumble || b.grounded) { b.thrownBy = NO_ID; continue; }
    for (let j = 0; j < world.fighters.length; j++) {
      const c = world.fighters[j]!;
      if (c === b || c.id === thrower) continue;
      if (c.state === FState.Spawning || c.state === FState.Respawning || c.state === FState.Falling || c.state === FState.Eliminated) continue;
      if (c.invulnUntil > tick || c.spawnProtUntil > tick || c.state === FState.Held || c.state === FState.Carried) continue;
      const dx = c.pos.x - b.pos.x, dz = c.pos.z - b.pos.z;
      if (hyp(dx, dz) > BODY_R + HURT_R) continue;
      if (!(b.pos.y < c.pos.y + 2.2 && c.pos.y < b.pos.y + 2.2)) continue;
      const sp = hyp(b.vel.x, b.vel.z);
      const nx = sp > 1e-6 ? b.vel.x / sp : dx, nz = sp > 1e-6 ? b.vel.z / sp : dz;
      const bi = T.GRAB.bodyImpact;
      world.systems.applyKnockback(world, c, nx, nz, bi.kb, bi.lift, bi.dmg, bi.stun, T.HITSTOP.bodyImpact, thrower);
      b.vel.x = 0; b.vel.z = 0;   // kendisi orada düşer
      b.hitstop = Math.max(b.hitstop, T.HITSTOP.bodyImpact);
      const e = world.events.push(Ev.BodyImpact, tick, b.id, c.id); e.x = b.pos.x; e.y = b.pos.y; e.z = b.pos.z;
      b.thrownBy = NO_ID;
      break;
    }
  }
}

// ───────────────────────────── adım 5 ─────────────────────────────
/** Tutan basılı/bırakma ve kurbanın mash'i; her tick bir tutan için. */
function stepHolder(world: SimWorld, a: Fighter): void {
  const tick = world.tick;
  const b = fighter(world, a.grabTarget);
  if (!b) { a.grabTarget = NO_ID; return; }
  const k = tick - a.stateTick;
  const inputB = world.inputs[b.id]!;
  const mashed = (inputB.pressed & FACE_BUTTONS) !== 0;
  switch (a.state) {
    case FState.Hold: {
      placeHeld(a, b);
      if (mashed) b.windupPresses++;
      if (b.grabStun < T.GRAB.windupEscapeMaxStun && b.windupPresses >= T.GRAB.windupEscapePresses) { escape(world, a, b, false); return; }
      const wantThrow = pressedSince(world, a, Btn.Grab, a.holdSince + 1);   // B tamponlanır (Hold başından sonra basılan)
      if (wantThrow && (b.grabStun >= T.GRAB.holdThrowInstantStun || k >= T.GRAB.holdLockTicks)) { startThrow(world, a, b); return; }
      if (k >= T.GRAB.holdLockTicks) {
        // B yok → omuza alma (Interact@0.65-1.30, 26 t); kurban 13. t'de sokete biner
        setState(a, FState.Lifting, tick, tick + T.GRAB.shoulderTicks + 1);
        a.holdSince = tick;
        world.events.push(Ev.PickUpFighter, tick, a.id, b.id);
      }
      break;
    }
    case FState.Lifting: {
      if (k < T.GRAB.liftAttachTick) placeHeld(a, b);
      else { if (b.state === FState.Held) setState(b, FState.Carried, tick); placeCarried(a, b); }
      if (mashed) b.windupPresses++;
      if (b.grabStun < T.GRAB.windupEscapeMaxStun && b.windupPresses >= T.GRAB.windupEscapePresses) { escape(world, a, b, k >= T.GRAB.liftAttachTick); return; }
      if (k >= T.GRAB.shoulderTicks) beginCarry(world, a, b);
      break;
    }
    case FState.PickingUp: {
      a.moveTick = k;
      a.stageIdx = k < T.GRAB.pickUpGripTick ? 0 : 1;
      if (a.stageIdx === 1 && a.stageStartTick !== a.stateTick + T.GRAB.pickUpGripTick) a.stageStartTick = a.stateTick + T.GRAB.pickUpGripTick;
      if (k === T.GRAB.pickUpGripTick) {
        // kavrama: kurban hâlâ yatıyor ve i-frame'de değilse Carried; Roll/GetUp ise ıska
        if ((b.state === FState.Lying || b.state === FState.Crawl) && b.invulnUntil <= tick) {
          b.grabStun = b.stun; b.windupPresses = 0; b.holdSince = tick;
          clearMove(b); setState(b, FState.Carried, tick);
          world.systems.items.drop(world, b, tick, Ev.Grab);
          world.events.push(Ev.Grab, tick, a.id, b.id);
        } else { detach(a, b); clearMove(a); setState(a, FState.Idle, tick); return; }
      }
      if (k > T.GRAB.pickUpGripTick) { a.phase = 3; placeCarried(a, b); }
      if (k >= T.GRAB.pickUpTotal) { clearMove(a); beginCarry(world, a, b); }
      break;
    }
    case FState.Carrying:
    case FState.Attack: {   // omuz çarpması sırasında kurban sokette kalır
      placeCarried(a, b);
      // Kurtulma (DESIGN §6, 5. tur): önce KORUMA PENCERESİ (escapeLeft = kalan tick), sonra MASH. Pencerede basış sayılmaz;
      // pencere dolunca kurban zamanla DEĞİL, yalnız `carryEscapePresses` basışla kurtulur — basmazsa kucakta kalır.
      // (Eski model sayaç sıfırlanınca kendiliğinden kurtarıyordu; oyuncu "kendi kurtuluyor" diye şikâyet etti.)
      if (b.escapeLeft > 0) b.escapeLeft--;
      else if (mashed) {
        b.escapePresses++;   // yalnız pencere sonrası artar → mash sesi/HUD'u (game.ts pollMashTick) ancak işe yarayan basışta öter
        if (b.escapePresses >= T.GRAB.carryEscapePresses) { escape(world, a, b, true); return; }
      }
      if (a.state !== FState.Carrying) break;
      // Süre aşımıyla otomatik bırakma KALDIRILDI (eski `carryUntil` 2.5 s): kullanıcı "basmazsa kucakta kalsın" dedi. Kilitlenme
      // riski yok — tutan L1 ile bırakır, B ile fırlatır, üçüncü kişi koparır, kurban mash'le çıkar.
      if (a.controlLockUntil > tick) break;
      if (pressedWithin(world, a, Btn.Block, T.INPUT.bufferTicks)) { consume(world, a, Btn.Block); dropCarried(world, a, T.KNOCKDOWN.carryDropLyingL1); return; }
      if (pressedWithin(world, a, Btn.Grab, T.INPUT.grabBufferTicks)) { startThrow(world, a, b); return; }
      break;
    }
    case FState.Throwing: {
      // kurban 0–18 t sokette (Held: önde; Carried: omuzda); tutan geri/yan yöne 6 t'de döner
      const target = yawOf(a.holdDirX, a.holdDirZ);
      const d = wrapAngle(target - a.yaw);
      const step = Math.PI / T.GRAB.holdTurnTicks;
      a.yaw = Math.abs(d) <= step ? target : wrapAngle(a.yaw + (d > 0 ? step : -step));
      a.moveTick = k; a.phase = k < T.GRAB.throwReleaseTick ? 1 : k === T.GRAB.throwReleaseTick ? 2 : 3;
      if (k < T.GRAB.throwReleaseTick) {
        if (b.state === FState.Held) {
          placeHeld(a, b);
          if (mashed) b.windupPresses++;
          if (b.grabStun < T.GRAB.windupEscapeMaxStun && b.windupPresses >= T.GRAB.windupEscapePresses) { escape(world, a, b, false); setState(a, FState.Hitstun, tick, tick + T.GRAB.holderStunTicks); return; }
        } else placeCarried(a, b);
      } else if (k === T.GRAB.throwReleaseTick) releaseThrow(world, a, b);
      break;
    }
    default:
      // tutan başka bir duruma düştü (Hitstun vb.) ama ilişki temizlenmemiş → kopar
      breakGrab(world, a, 0);
      break;
  }
}
function beginCarry(world: SimWorld, a: Fighter, b: Fighter): void {
  const tick = world.tick;
  setState(a, FState.Carrying, tick);
  a.carryUntil = NO_TICK;   // süre aşımı yok (bkz. Carrying dalı); alan match/tutorial temizliğiyle uyumlu kalsın diye duruyor
  if (b.state !== FState.Carried) setState(b, FState.Carried, tick);
  placeCarried(a, b);
  // Koruma penceresi grabStun'dan bağımsız sabit: eski T = 1.1 + stun/100 modelinde çok vurulmuş kurban DAHA GEÇ kurtuluyordu;
  // artık kurtulma tamamen oyuncunun elinde (mash), o yüzden tek ve okunabilir bir sayı yeterli.
  b.escapeLeft = T.GRAB.carryEscapeGraceTicks; b.escapePresses = 0;
  const e = world.events.push(Ev.Carry, tick, a.id, b.id); e.v0 = b.escapeLeft;
}

/** SimSystems.stepGrab — ARCHITECTURE §3 adım 5. */
export function stepGrab(world: SimWorld): void {
  const tick = world.tick;
  const fs = world.fighters;
  for (let i = 0; i < fs.length; i++) {
    const a = fs[i]!;
    if (a.state === FState.Escaping) {
      const k = tick - a.stateTick;
      if (k >= 1 && k <= ESCAPE_TICKS) { const ds = T.GRAB.escapeJump / ESCAPE_TICKS; a.rmDx += a.dodgeDirX * ds; a.rmDz += a.dodgeDirZ * ds; }
      if (tick >= a.stateUntil) setState(a, FState.Idle, tick);
      continue;
    }
    if (a.state === FState.Throwing && a.grabTarget === NO_ID && a.moveIdx === G2_IDX) {
      // bırakma sonrası toparlanma (15 t)
      a.moveTick = tick - a.stateTick; a.phase = 3;
      if (tick - a.stateTick >= THROW_TOTAL) { clearMove(a); setState(a, FState.Idle, tick); }
      continue;
    }
    if (a.grabTarget === NO_ID || a.hitstop > 0) continue;
    stepHolder(world, a);
  }
}

/** Kurbanın Carry koruma penceresinden kalan tick (HUD/bot); 0 → basışlar sayılmaya başladı. */
export const escapeLeftOf = (f: Fighter): number => f.escapeLeft;
/** Fırlatılan bedenin atanı (test/HUD). */
export const thrownByOf = (f: Fighter): number => f.thrownBy;
/** Windup kurtulma basış sayacı (test). */
export const windupPressesOf = (f: Fighter): number => f.windupPresses;
